import React, { useEffect, useContext } from 'react';
import cx from 'classnames';

import { randomArray } from 'utils/randomArray';

import { useDispatchingFiltered, useDispatchingSuggestions } from 'modules/dispatching/service';
import DispatchingListHeader from 'modules/dispatching/list-header';
import DispatchingListItem from 'modules/dispatching/list-item';

import AssignmentContext from 'modules/dispatching/assignment/context';
import { useDispatchingUI } from 'modules/dispatching/ui';

import { ReactComponent as EmptyStateIcon } from 'assets/icons/empty-state-gatekeeping.svg';

import { useRouting } from 'modules/routing';

import { matchSuggestions } from './suggestions';

const t = {
  no_results: 'There are no items to review',
  wide_search: 'Try changing the filters to widen your search',
};

const DispatchingList = ({ className, reduced }) => {
  const { itineraries, groupedItineraries, shipperOrderIds, mutate } = useDispatchingFiltered({
    suspense: true,
  });

  const { suggestions, mutateSuggestions } = useDispatchingSuggestions(shipperOrderIds);
  const suggestionMatches = matchSuggestions({
    itineraries,
    suggestions,
  });

  const { itineraryToAssign } = useContext(AssignmentContext);
  const { loading, setMutateListFn, setMutateSuggestionsListFn } = useDispatchingUI();

  const { usedItineraryIds: itinerariesAddedToRouting } = useRouting();

  const groupedItinerariesKeys = Object.keys(groupedItineraries);
  const hasItems = groupedItinerariesKeys.length > 0;

  useEffect(() => {
    setMutateListFn(() => mutate);
    setMutateSuggestionsListFn(() => mutateSuggestions);
  }, [setMutateListFn, mutate, setMutateSuggestionsListFn, mutateSuggestions]);

  if (!hasItems) {
    return (
      <div
        className={cx(`${className}__no-results`, {
          reduced: reduced,
        })}
      >
        <div className="icon">
          <EmptyStateIcon />
        </div>
        <div className="title">{t.no_results}</div>
        <div>{t.wide_search}</div>
      </div>
    );
  }

  return (
    <>
      <div className={cx(className)} data-testid="DispatchingList">
        {groupedItinerariesKeys.map((date) => (
          <div key={date}>
            <DispatchingListHeader date={date} total={groupedItineraries[date].length} />
            {groupedItineraries[date].map((itinerary) => (
              <DispatchingListItem
                key={itinerary.id}
                itinerary={itinerary}
                reduced={reduced}
                loading={!!loading[itinerary.id]}
                suggestionFound={suggestionMatches[itinerary.id]}
                isBlocked={
                  itineraryToAssign === itinerary.id ||
                  itinerariesAddedToRouting.includes(String(itinerary.id))
                }
              />
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

const DispatchingListEmpty = ({ className }) => {
  return (
    <div className={cx(className)}>
      <DispatchingListHeader loading />
      {randomArray(5, 20).map((key) => (
        <DispatchingListItem key={key} loading />
      ))}
    </div>
  );
};

export default DispatchingList;
export { DispatchingListEmpty };
